// About page data - Updated with real resume information
export const aboutData = {
  personal: {
    name: 'Jarrad Totman',
    title: 'Senior Developer & Technical Director',
    subtitle: 'Full-Stack & Game Development',
    phone: '1(207)330-0943',
    email: 'jarradtotman@gmail.com',
    website: 'jarradtotman.com',
  },
  bio: {
    title: 'Senior Developer with 16+ Years of Experience',
    paragraphs: [
      `Senior programmer with 16+ years of experience in software, game, and web development, 
      specializing in C++ and Unreal Engine. Led engineering efforts on a video game now valued 
      at $20M, demonstrating the ability to architect, ship, and scale complex systems from 
      prototype to live production.`,
      `Brings strong expertise in multiplayer systems, server-side architecture, and AWS 
      infrastructure, with a proven track record of leading distributed engineering teams. 
      With AAA studio experience, consistently delivers production-ready, scalable solutions 
      in high-stakes, fast-paced environments.`,
      `Specializing in full-stack development, AI integration, immersive 3D experiences, 
      and cutting-edge game development. My work combines technical expertise with creative 
      vision to deliver solutions that are both powerful and user-friendly.`,
    ],
  },
  skills: [
    { name: 'C++', level: 98 },
    { name: 'Unreal Engine 4/5', level: 95 },
    { name: 'React/TypeScript', level: 90 },
    { name: 'Python', level: 88 },
    { name: 'Multiplayer Networking', level: 95 },
    { name: 'AWS Infrastructure', level: 90 },
    { name: 'AI/ML Integration', level: 85 },
    { name: 'Three.js/WebGL', level: 85 },
  ],
  skillCategories: {
    core: {
      title: 'CORE GAME / ENGINEERING',
      items: [
        'Unreal Engine 4/5 (C++, Blueprint, UMG, Slate)',
        'Gameplay Systems, Architecture, Frameworks & UX/UI',
        'Multiplayer Networking, Replication & Client Prediction',
        'Dedicated Servers, Headless Builds & Orchestration',
        'Large-Scale Instance-Based Persistent Worlds',
        'Combat, Ability, Progression & Economy Systems',
        'Character, Vehicle, Locomotion, Physics & Collision',
        'AI Systems (State Machines, Behavior Trees, Utility AI)',
        'Animation (Montages, IK, Retargeting, Control Rig)',
        'VR/XR, Spatial Interfaces & Live Real-Time Simulation',
        'Live Events, Streaming & Synchronized Experiences',
      ],
    },
    programming: {
      title: 'PROGRAMMING',
      items: ['C++', 'C#', 'Python', 'JavaScript/TypeScript', 'Lua', 'Bash'],
    },
    devops: {
      title: 'DEVOPS / INFRASTRUCTURE',
      items: [
        'AWS (EC2, S3, IAM, CloudWatch, Autoscaling, Route53)',
        'CI/CD (Jenkins, GitHub Actions)',
        'Windows, Linux, Docker & Containerized Services',
        'Build Automation (Unreal UAT/UBT)',
        'Crash Reporting, Telemetry, Analytics & Observability',
        'Deployment, Rollback, Cost Optimization & Scaling',
        'Cross-Platform Dev & Networking (Firewalls, NAT, Ports)',
      ],
    },
    backend: {
      title: 'BACKEND / SYSTEMS',
      items: [
        'Server Integration & API Design',
        'Real-Time, Event-Driven & Distributed Systems',
        'WebSockets/TCP/UDP/Socket.io',
        'WebRTC (Audio, Video, Data Channels & Signaling)',
        'Authentication, Authorization & Tokens',
        'Matchmaking, Lobbies & Session Management',
        'Large-Scale Concurrent Chat & Messaging Systems',
        'Moderation Pipelines (Text/Image/Voice)',
        'High-Concurrency, Low-Latency Systems',
        'Persistence, State Sync & Datastores',
        'SQLite & Vector Databases (Qdrant)',
        'Server Authority, Anti-Cheat & Exploit Mitigation',
      ],
    },
    ai: {
      title: 'AI / AUTOMATION',
      items: [
        'OpenAI APIs (Text, Vision, Moderation)',
        'Voice Transcription (Whisper, FasterWhisper)',
        'AI Moderation, Trust & Safety Systems',
        'Image Analysis, Captioning & OCR',
        'Prompt Engineering & Cost Optimization',
        'LLM Serving (vLLM)',
        'Quantization (AWQ, GPTQ, FP4)',
        'CUDA/GPU Optimization & Inference Tuning',
        'Procedural Content Generation & AI Tooling',
      ],
    },
    techArt: {
      title: 'TECH ART / RENDERING',
      items: [
        'HLSL & Custom Shaders',
        'PBR Materials & Rendering Pipelines',
        'VFX & Real-Time Effects',
        'Environment Integration & Optimization',
        '3D Asset Pipelines, Rigging & Skinning',
        'Procedural Meshes, Splines, Geometry & World Building',
        'Linear Algebra, Vector Math & Transform Systems',
        'Three.js/WebGL',
        'Image-to-3D, Stable Diffusion, NeRF',
      ],
    },
    metaverse: {
      title: 'WORLD BUILDING / METAVERSE',
      items: [
        'Massive Multiplayer & Social Worlds',
        'Instance-Based, Persistent & Streaming Architectures',
        'Real-Time Collaboration, Shared State & UX Systems',
        'Live Ops, Events & World Updates',
        'Economy, Virtual Assets & UGC Pipelines',
      ],
    },
    web: {
      title: 'WEB / FULL STACK / WEB3',
      items: [
        'REST APIs & Full-Stack Development',
        'React & TypeScript (SPA Architecture & UI State)',
        'Data Ingestion & Processing Pipelines',
        'Real-Time Web Apps, Live Chat & Presence Systems',
        'WebRTC Signaling & Media Transport',
        'Web3 Integration & Wallet Connectivity',
        'Ethereum Smart Contracts (Solidity Concepts)',
        'On-Chain/Off-Chain State & Asset Sync',
      ],
    },
  },
  timeline: [
    {
      year: '2025-2026',
      company: 'DPLN.AI',
      role: 'Technical Director / Full-Stack Developer / AI Systems Architect',
      location: '',
      description: [
        'Architected comprehensive AI system with clinical-grade psychological analysis using Big Five, Myers-Briggs, Enneagram, DISC profiling, defense mechanisms, attachment styles, and cognitive bias detection',
        'Built Ultimate Gossip System with advanced credibility scoring, sentiment analysis, network mapping, and real-time social dynamics monitoring across 9 different gossip pattern types',
        'Developed Ultimate Psychological System providing complete personality profiling, emotional intelligence assessment, and therapeutic recommendations with risk detection for psychological crises',
        'Created advanced reasoning engine with chain-of-thought, tree-of-thought, and planning reasoning for complex problem-solving and goal-oriented behavior',
        'Implemented comprehensive voice system with wake word detection, real-time speech transcription using Whisper, Harley Quinn-style text-to-speech, and Discord voice channel integration',
        'Built full Discord bot integration with message monitoring, user profiling, real-time chat analysis, command system, and seamless voice channel participation',
        'Developed AI-powered 3D architecture generation system with dynamic building creation, road network generation, city block design, and platform-specific export for Unreal Engine, Unity, and Second Life',
        'Created Ultimate Unicode Processor with fuzzy matching, variant extraction, and emoji processing for robust user identification across all Unicode variations',
        'Built comprehensive dashboard with real-time WebSocket streaming, interactive network visualizations, social network analysis, heat maps, and multi-terminal system control',
        'Integrated Firestorm (Second Life) bot with native C++ core, authenticated session management, in-world game systems, and encrypted command processing',
        'Developed proactive intelligence system with predictive analytics, behavioral prediction models, early warning alerts, and adaptive learning algorithms',
        'Implemented Ultimate People Information System with demographic analysis, interest categorization, skill assessment, relationship mapping, and behavioral pattern recognition',
        'Created specialized analysis capabilities with linguistic analysis, temporal pattern recognition, social network analysis, and anomaly detection',
        'Designed multimodal analysis system with image processing, text analysis, voice transcription, and comprehensive information synthesis',
        'Managed full AWS infrastructure deployment with cost optimization, horizontal scaling, and comprehensive monitoring systems'
      ],
      links: ['https://dpln.ai']
    },
    {
      year: '2020-2026',
      company: 'THUG.TV',
      role: 'Technical Director / Full-Stack Developer / Metaverse Architect',
      location: '',
      description: [
        'Architected and developed comprehensive metaverse platform built on Unreal Engine 5 with immersive 3D experiences',
        'Built proprietary chat and friends system with real-time messaging, presence tracking, and social networking features',
        'Developed user-generated content system with runtime upload capabilities, enabling community-driven content creation',
        'Created inventory system inspired by Second Life, allowing extensive customization and personalization',
        'Designed and implemented THUG WALLET - proprietary cryptocurrency ecosystem with blockchain-based transactions',
        'Developed THUG LAND - NFT property contract system with tiered membership structures for virtual land ownership',
        'Built comprehensive REST API for third-party integration, enabling plugins, mods, and extensions',
        'Architected full-stack web application with React, TypeScript, and modern UI/UX design',
        'Implemented multiplayer networking, replication, and dedicated server infrastructure',
        'Managed AWS cloud infrastructure with EC2, S3, RDS, ALB, Route 53, and cost-optimized deployment architecture',
        'Developed client application with Steam/Discord-like functionality for unified gaming ecosystem',
        'Integrated WebRTC for real-time audio/video communication and data channels',
        'Built authentication, authorization, and session management systems',
        'Implemented real-time chat systems with moderation pipelines and high-concurrency messaging'
      ],
      links: ['https://thug.tv']
    },
    {
      year: '2025',
      company: 'Apeing DEX',
      role: 'Technical Director / Fullstack Developer',
      location: '',
      description: [
        'Amazon IVS video streaming services for live streaming/trade platform',
        'TradingView & Trading components for live trading during streams',
        'Elixir / Phoenix chat with full custom administrative tools',
        'Full seamless web3 wallet integration for account persistence',
        'Executed the creation of multiple concepts from prototype to production',
        'Provided full AWS management and backend service'
      ],
      links: []
    },
    {
      year: '2024',
      company: 'Axhorn Games',
      role: 'Senior Unreal Developer',
      location: 'Istanbul, Türkiye',
      description: [
        'Provided extensive hands-on development, guidance and direction for the game BATTLEROOM',
        'Created proprietary Steam-based host migration system and game browser',
        'Integrated dynamic peer to peer data structure for persistent game migration',
        'Integrated Steamworks API',
        'Managed Unreal Engine version updates, handling related errors and warnings',
        'Provided extensive debugging with characters and gameplay, handled troubleshooting and patching',
        'Provided extensive perforce management and debugging',
        'Managed multiplayer network capabilities and thoroughly tested deployments',
        'Provided tutoring and guidance to international employees through translation'
      ],
      links: []
    },
    {
      year: '2023',
      company: 'ALONGSIDE/ Global',
      role: 'Technical Director / Senior Lead Developer',
      location: 'London, England, United Kingdom',
      description: [
        'Led comprehensive management of network infrastructure and servers, overseeing the setup and administration of multiple Perforce servers',
        'Provided personalized tutoring sessions to enhance employee skill sets and productivity',
        'Pioneered the creation of an ultra-realistic XR environment for live production television commercials, blending virtual and real-world elements',
        'Provided creative and technical direction, guiding projects from conception to execution with precision and innovation'
      ],
      links: []
    },
    {
      year: '2023',
      company: 'Sombra Labs',
      role: 'Technical Director / Senior Lead Developer',
      location: '',
      description: [
        'Spearheaded development for TOXIC TWIGHTS',
        'Engineered a scalable dynamic scoreboard and implemented a dynamic locomotion system',
        'Developed fully runtime dynamic cover/vault/climbing system',
        'Created entire base functionality for game from main menu functionality, to server infrastructure and all core gameplay mechanics'
      ],
      links: ['https://www.youtube.com/watch?v=AjX8qSH2Ouk']
    },
    {
      year: '2023',
      company: 'Embers Park',
      role: 'Technical Director / Senior Lead Developer',
      location: 'Miami, Florida, United States',
      description: [
        'Developed immersive interactive environments for live events using cutting-edge technology',
        'Led the creation of a previsualization test level, integrating in-game cameras as textures on walls for real-time display simulations on LED walls',
        'Engineered unique particle and animation effects from real-world items',
        'Translated conceptual AI art into features within Unreal Engine 5 through custom blueprint and C++ work'
      ],
      links: []
    },
    {
      year: '2022',
      company: 'SHRAPNEL',
      role: 'Senior C++ Developer',
      location: 'Los Angeles, California, United States',
      description: [
        'Developed and implemented custom C++ extraction point game mode for SHRAPNEL FPS',
        'Spearheaded player data integration efforts, ensuring seamless connectivity and synchronization across gaming platforms',
        'Conducted extensive debugging and networking tasks to optimize game performance and enhance overall user experience'
      ],
      links: ['https://shrapnel.com/']
    },
    {
      year: '2021-2022',
      company: 'Onessus',
      role: 'Technical Director / Senior Lead Developer',
      location: '',
      description: [
        'Led development initiatives for both NIFTYVILLE and HODLGOD projects, overseeing a team of 60+ members and spearheading project planning efforts',
        'Executed extensive and robust custom artificial intelligence work to enhance game functionality and user experience',
        'Managed multiplayer networking and replication, as well as server management and architecture utilizing AWS services'
      ],
      links: ['https://niftyville.net/', 'https://hodlgod.com/', 'https://onessus.com/']
    },
    {
      year: '2021-2022',
      company: '3D CityScapes',
      role: 'Senior Lead Developer',
      location: 'Toronto, Ontario, Canada',
      description: [
        'Led the development efforts for PINKSLIP CITY, overseeing all aspects of the project from conception to execution',
        'Pioneered the porting of the Unreal Engine 4 Advanced Vehicle Plugin to Unreal Engine 5, establishing a global milestone as the first to achieve this feat',
        'Engineered a hyper-realistic Porsche, complete with a sophisticated launch control system and immersive FMod sound design',
        'Managed multiplayer networking and replication, along with server management and architecture leveraging AWS infrastructure'
      ],
      links: ['https://3dcityscapes.ca/', 'https://youtu.be/2OS3iCxV9_4']
    },
    {
      year: '2021-2022',
      company: 'Hypersonic Laboratories, Inc.',
      role: 'Technical Director / Senior Lead Developer',
      location: '',
      description: [
        'Led the development of HELIX METAVERSE, a project that has since appreciated to a value exceeding twenty million dollars',
        'Engineered custom pedestrian and vehicle traffic AI solutions to enhance immersive gameplay experiences',
        'Designed and implemented a hyper-realistic drivable McLaren within Unreal Engine 5, incorporating custom speedometer functionality and innovative Watch Dogs-inspired object interaction features',
        'Integrated Metahuman technology seamlessly and conducted extensive UI enhancements to optimize user engagement',
        'Managed replication and multiplayer networking systems to ensure seamless online interactions',
        'Directed and oversaw a team of over 20 employees, providing strategic leadership and project management expertise'
      ],
      links: ['https://helixmetaverse.com/']
    },
    {
      year: '2021',
      company: 'Mars4',
      role: 'Senior C++ Developer',
      location: '',
      description: [
        'Engineered the server architecture for MARS4, ensuring robust performance and scalability',
        'Seamlessly integrated the Open World Server, implementing a load-balanced land server grid for uninterrupted gameplay experiences',
        'Developed innovative solutions for seamless server travel, multiplayer networking, and replication'
      ],
      links: ['https://www.mars4.me/']
    },
    {
      year: '2020-2021',
      company: 'Crouch Jump Interactive',
      role: 'Lead Developer',
      location: '',
      description: [
        'Led the development efforts for MORTARMEN, overseeing all aspects of the project from conception to completion',
        'Engineered custom C++ solutions for slope-and-velocity-based fall damage and ragdoll character physics, enhancing realism and immersion within the game environment',
        'Implemented replication and multiplayer networking functionalities to enable seamless online interactions',
        'Conducted extensive debugging to ensure optimal performance and stability across all game components'
      ],
      links: ['https://gomortarmen.com/']
    },
    {
      year: '2019-2020',
      company: 'Snowchimp Interactive',
      role: 'Lead Developer',
      location: '',
      description: [
        'As the lead in technical development for SHORES OF PLUNDER, orchestrated the creation of immersive gaming experiences',
        'Delved deeply into artificial intelligence, particularly in crafting challenging PvE combat encounters',
        'Ensured seamless replication in multiplayer settings'
      ],
      links: ['https://store.steampowered.com/app/1567190/Shores_of_Plunder/']
    },
    {
      year: '2019-2020',
      company: 'Koryu Studios',
      role: 'Lead Developer',
      location: '',
      description: [
        'As the driving force behind the development of SHOTGUN SAMURAI, oversaw all aspects of the project from inception to completion',
        'Implemented extensive AI for engaging PvE combat encounters and ensured seamless replication in multiplayer settings',
        'Personally handled custom 3D modeling, rigging, texturing, animation, mapping, and sound design to bring the game\'s world to life with meticulous detail and authenticity'
      ],
      links: ['https://twitter.com/KoryuStudios']
    },
    {
      year: '2018-2019',
      company: 'Canadapt Studio Inc.',
      role: 'Lead Developer',
      location: 'Toronto, Ontario, Canada',
      description: [
        'As the leader in spearheading the development of HARDKNOCK NIGHTS, directed the project from conception to realization',
        'Designed and implemented a global leaderboard, seamlessly integrating it with MySQL for efficient data management',
        'Developed a custom instant replay and highlight system complete with media controls, enhancing the user experience',
        'Leveraged Unreal Engine to integrate RAZER fight stick compatibility, broadening accessibility for players',
        'Contributed to refining fighting mechanics, optimizing multiplayer networking, and crafting intuitive UI and Main Menu designs'
      ],
      links: ['https://web.archive.org/web/20200911072449/https://www.canadaptstudio.ca/hnl']
    },
    {
      year: '2017-2018',
      company: 'Now, Winners Announced',
      role: 'C++ Developer',
      location: '',
      description: [
        'Contributed to the development team for BULLETVILLE, focusing on the creation of various sci-fi vehicles including hovercraft, hoverboards, hoverbuggies, and hoverbikes',
        'Utilized coding and physics expertise to ensure realistic and engaging vehicle mechanics',
        'Played a key role in implementing multiplayer networking and replication functionalities, while also conducting thorough debugging to maintain optimal performance and stability across all aspects of the game'
      ],
      links: ['https://www.nowwa.com/', 'https://www.bulletville.com/']
    },
    {
      year: '2016-2017',
      company: 'Iron City Games',
      role: 'C++ Developer',
      location: 'Birmingham, Alabama, United States',
      description: [
        'Contributed to the development team for GEARSTORM, playing a pivotal role in various aspects of the project',
        'Designed and implemented a custom 128-player interactive scoreboard and squad system, enhancing player engagement and teamwork dynamics',
        'Conducted vehicle physics work and programmed weapons and characters to ensure immersive gameplay experiences',
        'Responsible for extensive debugging to maintain the game\'s stability and performance',
        'Contributed to web and graphics development, as well as UI design, utilizing Unreal Blueprint to bring creative visions to life'
      ],
      links: ['https://ironcitygames.com/']
    },
  ],
  values: [
    {
      icon: 'heart',
      title: 'Passion',
      description: '16+ years of passion for game development, software engineering, and creating exceptional digital experiences.',
    },
    {
      icon: 'target',
      title: 'Leadership',
      description: 'Proven track record of leading distributed engineering teams and delivering production-ready solutions.',
    },
    {
      icon: 'award',
      title: 'Excellence',
      description: 'Led engineering on a $20M valued game, demonstrating ability to architect and scale complex systems.',
    },
    {
      icon: 'lightbulb',
      title: 'Innovation',
      description: 'Constantly pushing boundaries with AI integration, immersive 3D experiences, and cutting-edge technologies.',
    },
  ],
}
